import { useState, useEffect, useCallback, useRef, ReactNode, RefObject, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';

import { KnmTypeCodesEnum } from 'APIPackage/CatalogsModule/enums/KnmTypeCodesEnum';
import { KnmKindCodesEnum } from 'APIPackage/DictionariesModule/enums/KnmKindCodesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { KnmErknmMenu } from 'KnmErknmPackage/KnmErknmMenuModule/components/KnmErknmMenu';
import { ErknmMenuItemsIdsEnum } from 'KnmErknmPackage/KnmErknmMenuModule/enums/ErknmMenuItemsIdsEnum';
import {
  knmSetChangePlanReasonAfterConfirmEvent,
  knmSetOpenChangeKnmModal,
} from 'KnmPackage/MainModule/actions/knmActions';
import { ChangeKnmModal } from 'KnmPackage/MainModule/components/ChangeKnmModal';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { ErknmAttachWebDocumentModal } from 'KnmPackage/MainModule/components/ErknmAttachWebDocumentModal';

import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { KnmErknmContent } from './KnmErknmContent';
import { IKnmErknmFormChangePlanReason } from '../interfaces/IKnmErknmFormChangePlanReason';
import { useKnmFormStatusHintInit } from '../../../KnmPackage/MainModule/hooks/useKnmFormStatusHintInit';
import { IAdminSetting } from '../../../APIPackage/AdminModule/interfaces/IAdminSetting';
import { IKnmFormRenderProps } from '../../../KnmPackage/MainModule/components/KnmForm';

export interface IKnmErknmFormRenderProps {
  knmData?: IKnm;
  formRef: IKnmFormRef;
  bodyRef: RefObject<HTMLDivElement>;
  values: IKnmErknmForm;
  lateChangesFieldLinks: Map<string, string>;
  renderForm: (menu: ReactNode, content: ReactNode) => ReactNode;
  touched: IKnmFormRenderProps['touched'];
  settings: IAdminSetting[];
}

export const KnmErknmFormRender = ({
  knmData,
  formRef,
  bodyRef,
  values,
  renderForm,
  touched,
  settings,
  lateChangesFieldLinks,
}: IKnmErknmFormRenderProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();

  useKnmFormStatusHintInit({ values, touched, settings });

  const isPlanType = knmData?.type?.code === KnmTypeCodesEnum.PP;
  const isVPType = knmData?.type?.code === KnmTypeCodesEnum.VP;
  const formKindKnmCode = values.kindKnm?.code;
  const formObjects = values.objectsErknm;
  const formObjectsRef = useRef<Array<{ listId: string; isNew?: boolean }> | undefined>(formObjects);

  formObjectsRef.current = formObjects;

  const isKnmErknmOtherInfoVisible = formKindKnmCode === KnmKindCodesEnum.MZ || formKindKnmCode === KnmKindCodesEnum.VK;

  const hiddenErknmMenuItemsIds: ErknmMenuItemsIdsEnum[] = useMemo(() => {
    const newHiddenMenuItemsIds: ErknmMenuItemsIdsEnum[] = [];

    if (!isKnmErknmOtherInfoVisible) {
      newHiddenMenuItemsIds.push(ErknmMenuItemsIdsEnum.KNM_OTHER_INFO);
    }
    if (!isPlanType) {
      newHiddenMenuItemsIds.push(ErknmMenuItemsIdsEnum.KNM_PLAN_HISTORY);
      newHiddenMenuItemsIds.push(ErknmMenuItemsIdsEnum.KNM_PLAN_HISTORY_CHANGES);
    }

    return newHiddenMenuItemsIds;
  }, [isKnmErknmOtherInfoVisible, isPlanType]);

  const [isChangeKnmModalVisible, setIsChangeKnmModalVisible] = useState(false);
  const openChangeKnmModal = useCallback(() => setIsChangeKnmModalVisible(true), []);
  const closeChangeKnmModal = useCallback(() => setIsChangeKnmModalVisible(false), []);
  const setOpenChangeKnmModal = useCallback(
    (openChangeKnmModalFn: () => void) => dispatch(knmSetOpenChangeKnmModal(openChangeKnmModalFn)),
    [dispatch],
  );

  useEffect(() => {
    setOpenChangeKnmModal(openChangeKnmModal);
  }, [openChangeKnmModal, setOpenChangeKnmModal]);

  const handleChangeKnmModalApply = useCallback(
    (changePlanReasonAfterConfirm: IKnmErknmFormChangePlanReason[]) => {
      closeChangeKnmModal();
      dispatch(knmSetChangePlanReasonAfterConfirmEvent(changePlanReasonAfterConfirm));
    },
    [closeChangeKnmModal],
  );

  const menu = (
    <KnmErknmMenu
      dataRef={bodyRef}
      hiddenItemsIds={hiddenErknmMenuItemsIds}
      knmType={knmData?.type}
      lateChangesFieldLinks={lateChangesFieldLinks}
    />
  );

  const content = <KnmErknmContent formRef={formRef} knmData={knmData} values={values} />;

  return (
    <>
      {renderForm(menu, content)}
      {isPlanType && (
        <ChangeKnmModal
          visible={isChangeKnmModalVisible}
          typeFieldValue={values.typeErknm}
          kindKnmFieldValue={values.kindKnm}
          onApply={handleChangeKnmModalApply}
          onClose={closeChangeKnmModal}
          objectsErknm={values.objectsErknm}
        />
      )}
      {isVPType && <ErknmAttachWebDocumentModal />}
    </>
  );
};
